// @ts-nocheck
import type {EventTemplate} from 'src/api-parsers/events';
import {IndexStore} from 'src/stores/index';

import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import ReactDOM from 'react-dom';
import {Helmet} from 'react-helmet';
import {provideHooks} from 'src/flux/provideHooks';
import flow from 'lodash/flow';
import debounce from 'lodash/debounce';
import {connect, useSelector} from 'react-redux';

import {
  getEventTemplate,
  deleteEventTemplate,
  updateEventTemplate,
  copyEventTemplate,
} from 'src/actions/event-template';
import {showGenericError} from 'src/action-creators/modal';
import {selectCorporateLabels} from 'src/selectors/language-support';

import {fluxify} from 'src/flux/component';
import Loading from 'src/components/lib/loading/loading';
import Breadcrumbs from 'src/components/lib/breadcrumbs';
import Variables from './variables';
import Edit from './edit';
import DevicePreview from 'src/components/lib/device-preview';
import I18nContext from 'src/contexts/i18n-context';

import css from './container.css';
import crumbsCss from 'src/components/lib/breadcrumbs/index.css';

// using HOC to inject the `t` function into the class component
function withI18n(Component: unknown) {
  return function WrapperComponent(props: unknown) {
    const {t, makeKey} = useLabelTranslation();
    const corporateLabels = useSelector(selectCorporateLabels);
    return (
      <Component
        {...props}
        t={t}
        makeKey={makeKey}
        corporateLabels={corporateLabels}
      />
    );
  };
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  showErrorModal: (t) => {
    dispatch(
      showGenericError({
        title: t(
          'This_touchpoint_template_is_currently_in_use',
          'This touchpoint template is currently in use.',
        ),
        text: `${t(
          'To_delete_this_touchpoint_template_you\'ll_first_need_to_remove_it_from_any_journey_touchpoints_that_are_using_it',
          'To delete this touchpoint template, you\'ll first need to remove it from any journey touchpoints that are using it.',
        )}`,
      }),
    );
  },
});

const dependencies = provideHooks({
  deferred: ({store, params: {templateId}}) =>
    getEventTemplate(store, templateId),
});

const mappers = {
  template: (store, {params: {templateId}}) =>
    store.eventTemplates.get(templateId),
} as const;

type TemplateDetailProps = {
  template: EventTemplate;
  store: IndexStore;
  showErrorModal: (t: (key: string, value: string) => string) => any;
  children: any;
  router: any;
  location: any;
  corporateLabels: {
    [key: string]: string;
  };
  t: (key: string, value: string) => string;
  makeKey: (key: string) => string;
};

class TemplateDetail extends React.Component<TemplateDetailProps> {
  editor: null | Edit | Variables;
  iframe: HTMLIFrameElement | null | undefined;
  static contextType = I18nContext;

  state = {
    saving: false,
  };

  constructor(props: unknown) {
    super(props);

    (this as any).handleDeleteTemplate = this.handleDeleteTemplate.bind(this);
    (this as any).handleDuplicateTemplate =
      this.handleDuplicateTemplate.bind(this);
    (this as any).handleSave = this.handleSave.bind(this);
    (this as any).handleSaveDebounced = debounce(this.handleSave, 750);
  }

  render() {
    const {template, t, makeKey} = this.props;
    if (!template) {
      return <Loading />;
    }
    const crumbs = [
      {
        url: '/settings/templates',
        name: t(
          makeKey(this.props.corporateLabels['email_templates']),
          'Templates',
        ),
      },
      {url: `/settings/templates/${template.id}`, name: template.name},
    ];

    // TODO (kyle): figure this out
    const above = true;
    const isCustom = template.templateType === 'custom';

    return (
      <div className={css.detailContainer}>
        <Helmet title={`${t('Templates', 'Templates')} - ${template.name}`} />
        <div className={crumbsCss.titleBox}>
          <h1 className={above ? css.title : crumbsCss.titleFixed}>
            <Breadcrumbs crumbs={crumbs} />
            <div className={css.actions}>
              <button
                className={css.actionButton}
                onClick={this.handleDeleteTemplate}
              >
                {t('Delete', 'Delete')}
              </button>
              <span className={css.actionSlash}>/</span>
              <button
                className={css.actionButton}
                onClick={this.handleDuplicateTemplate}
              >
                {t('Duplicate', 'Duplicate')}
              </button>
              {isCustom && (
                <button
                  className={css.saveButton}
                  onClick={this.handleSave}
                  disabled={this.state.saving}
                >
                  {this.state.saving
                    ? `${t('Saving', 'Saving')}...`
                    : t('Save', 'Save')}
                </button>
              )}
            </div>
          </h1>
        </div>

        <div className={isCustom ? css.customBody : css.body}>
          <DevicePreview className={css.preview} initialDevice="desktop">
            {({device}) => (
              <div>
                {device === 'mobile' ? (
                  <div>
                    <iframe
                      src={`/api/v1/event-templates/${template.id}/html`}
                      className={css.mobilePreview}
                      marginHeight="0px"
                      marginWidth="0px"
                      frameBorder="0"
                      scrolling="yes"
                      ref={(el) => (this.iframe = el)}
                      sandbox="allow-same-origin"
                    />
                    <div
                      className={css.overshade}
                      onClick={(e) => e.preventDefault()}
                    />
                  </div>
                ) : (
                  <FullHeightIframe
                    templateId={template.id}
                    className={css.desktopPreview}
                    iframeRef={(el) => (this.iframe = el)}
                  />
                )}
              </div>
            )}
          </DevicePreview>

          {isCustom ? (
            <Edit
              key={template.id}
              template={template}
              fluxRef={(component) => (this.editor = component)}
            />
          ) : (
            <Variables
              template={template}
              fluxRef={(component) => (this.editor = component)}
              onChange={this.handleSaveDebounced}
            />
          )}
        </div>
      </div>
    );
  }

  handleDeleteTemplate() {
    const {
      store,
      showErrorModal,
      template: {id},
      t,
    } = this.props;
    deleteEventTemplate(store, id).then((resp) => {
      if (resp) {
        this.props.router.push('/settings/templates');
      } else {
        showErrorModal(t);
      }
    });
  }

  handleDuplicateTemplate() {
    const {
      store,
      template: {id},
      router,
    } = this.props;
    const {t} = this.props;
    const payload = {
      name_suffix: t('Copy', 'Copy'),
    };
    copyEventTemplate(store, id, payload).then((resp) => {
      if (resp) {
        router.push(`/settings/templates/${resp.id}/`);
      }
    });
  }

  handleSave(reload: boolean = true) {
    if (!this.editor) {
      return;
    }

    // Prevent double submission
    if (this.state.saving) {
      return;
    }

    this.setState({saving: true});

    const template = this.editor.getTemplate();
    const updatedTemplate = {...this.props.template, ...template} as const;

    updateEventTemplate(this.props.store, updatedTemplate)
      .then(() => {
        this.setState({saving: false});
        if (this.iframe && reload) {
          this.iframe.contentWindow.location.reload();
        }
      })
      .catch((error) => {
        this.setState({saving: false});
        console.error('Failed to save template:', error);
        // You might want to show a toast or error modal here
      });
  }
}

type IframeProps = {
  templateId: string;
  iframeRef: () => any;
  className?: string;
  offset?: number;
};

class FullHeightIframe extends React.Component<IframeProps> {
  static defaultProps = {
    offset: 20,
  };

  state = {
    iFrameHeight: '0px',
  };

  iframeRef = React.createRef();

  handleLoad = () => {
    if (this.iframeRef.current) {
      const iframeDoc = this.iframeRef.current.contentWindow.document;
      this.setState({
        iFrameHeight: iframeDoc.body.scrollHeight + (this.props.offset || 0),
      });
    }
  };

  render() {
    const {templateId, className, iframeRef} = this.props;

    return (
      <iframe
        onLoad={this.handleLoad}
        ref={(el) => {
          this.iframeRef.current = el;
          iframeRef(el);
        }}
        src={`/api/v1/event-templates/${templateId}/html`}
        scrolling="no"
        marginHeight="0px"
        marginWidth="0px"
        frameBorder="0"
        className={className}
        id="iframe"
        style={{
          maxHeight: this.state.iFrameHeight,
          height: this.state.iFrameHeight,
        }}
        sandbox="allow-same-origin"
      />
    );
  }
}

export default TemplateDetail = flow(
  fluxify(mappers),
  connect(null, mapDispatchToProps),
  dependencies,
)(withI18n(TemplateDetail));
